import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    Badge,
    UncontrolledTooltip,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane
} from 'reactstrap';
import {
    ChefHat,
    LayoutGrid,
    Info,
    CheckCircle2,
    GripVertical,
    Minus,
    Split,
    ListOrdered
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { useKitchenStore } from '../../../../stores/Assets/kitchen';
import { useSpaceStore } from '../../../../stores/Assets/space';
import { useMenuStore } from '../../../../stores/Assets/menu';

const THEME_COLOR = '#c59253';

const DistributionWizardModal = ({ isOpen, toggle, siteId }) => {
    const navigate = useNavigate();
    const { kitchens, getKitchensBySite } = useKitchenStore();
    const { spaces, getSpaces } = useSpaceStore();
    const { menus, getMenus } = useMenuStore();

    const [activeTab, setActiveTab] = useState('1');
    const [stations, setStations] = useState([]);
    const [unassignedSpaces, setUnassignedSpaces] = useState([]);
    const [unassignedMenus, setUnassignedMenus] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [dragOverStationId, setDragOverStationId] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(true);

    const [activeSpaceId, setActiveSpaceId] = useState(null);
    const [spaceMenuLinks, setSpaceMenuLinks] = useState({}); // { spaceId: [menuId1, menuId2] }

    useEffect(() => {
        if (isOpen && siteId) {
            loadInitialData();
        }
    }, [isOpen, siteId]);

    const loadInitialData = async () => {
        setIsLoadingData(true);
        try {
            const [kRes, sRes, mRes] = await Promise.all([
                getKitchensBySite(siteId, { page: 1, perPage: 100 }),
                getSpaces(siteId, { page: 1, perPage: 100 }),
                getMenus(siteId)
            ]);

            const remoteKitchens = kRes?.kitchens || [];
            const remoteSpaces = sRes?.spaces || [];
            const remoteMenus = menus || [];

            // Map backend data to station-centric local state
            const stationsData = remoteKitchens.map(k => ({
                id: k.kitchen_id,
                name: k.name,
                assignedSpaces: remoteSpaces.filter(s => s.kitchen_id === k.kitchen_id).map(s => s.space_id),
                assignedMenus: remoteMenus.filter(m => m.kitchen_id === k.kitchen_id).map(m => m.menu_id)
            }));

            setStations(stationsData);
            setUnassignedSpaces(remoteSpaces.filter(s => !s.kitchen_id).map(s => ({ id: s.space_id, name: s.name })));
            setUnassignedMenus(remoteMenus.filter(m => !m.kitchen_id).map(m => ({ id: m.menu_id, name: m.name })));

            // Initialize Menu Sequence Data
            const sequenceData = {};
            remoteSpaces.forEach(space => {
                const assignedMenus = remoteMenus
                    .filter(m => m.kitchen_id === space.kitchen_id || (space.menuLinks || []).some(ml => ml.menu_id === m.menu_id))
                    .map(m => m.menu_id);
                sequenceData[space.space_id] = assignedMenus;
            });
            setSpaceMenuLinks(sequenceData);
            if (remoteSpaces.length > 0) setActiveSpaceId(remoteSpaces[0].space_id);

        } catch (error) {
            console.error("Failed to load distribution data:", error);
        } finally {
            setIsLoadingData(false);
        }
    };

    const onMenuSortEnd = (result) => {
        if (!result.destination || !activeSpaceId) return;

        const currentOrder = Array.from(spaceMenuLinks[activeSpaceId] || []);
        const [removed] = currentOrder.splice(result.source.index, 1);
        currentOrder.splice(result.destination.index, 0, removed);

        setSpaceMenuLinks(prev => ({
            ...prev,
            [activeSpaceId]: currentOrder
        }));
    };

    const toggleSelection = (id, type) => {
        setSelectedItems(prev => {
            const exists = prev.find(item => item.id === id && item.type === type);
            if (exists) return prev.filter(item => !(item.id === id && item.type === type));
            return [...prev, { id, type }];
        });
    };

    const handleDragStart = (e, items) => {
        e.dataTransfer.setData('items', JSON.stringify(items));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDrop = (e, stationId) => {
        e.preventDefault();
        setDragOverStationId(null);
        const data = e.dataTransfer.getData('items');
        if (!data) return;

        const droppedItems = JSON.parse(data);
        const updatedStations = stations.map(station => {
            if (station.id === stationId) {
                const newSpaces = droppedItems.filter(i => i.type === 'space').map(i => i.id);
                const newMenus = droppedItems.filter(i => i.type === 'menu').map(i => i.id);
                // Remove duplicates if any
                return {
                    ...station,
                    assignedSpaces: [...new Set([...station.assignedSpaces, ...newSpaces])],
                    assignedMenus: [...new Set([...station.assignedMenus, ...newMenus])]
                };
            }
            // Remove the dropped items from other stations if they were assigned elsewhere
            return {
                ...station,
                assignedSpaces: station.assignedSpaces.filter(id => !droppedItems.some(di => di.id === id && di.type === 'space')),
                assignedMenus: station.assignedMenus.filter(id => !droppedItems.some(di => di.id === id && di.type === 'menu'))
            };
        });

        const droppedSpaceIds = droppedItems.filter(i => i.type === 'space').map(i => i.id);
        const droppedMenuIds = droppedItems.filter(i => i.type === 'menu').map(i => i.id);

        setStations(updatedStations);
        setUnassignedSpaces(prev => prev.filter(s => !droppedSpaceIds.includes(s.id)));
        setUnassignedMenus(prev => prev.filter(m => !droppedMenuIds.includes(m.id)));
        setSelectedItems([]);
    };

    const unassignItem = (stationId, itemId, type) => {
        const updatedStations = stations.map(station => {
            if (station.id === stationId) {
                const listKey = type === 'space' ? 'assignedSpaces' : 'assignedMenus';
                return { ...station, [listKey]: station[listKey].filter(id => id !== itemId) };
            }
            return station;
        });

        setStations(updatedStations);

        // Find item name to add back to unassigned
        if (type === 'space') {
            const space = spaces.find(s => s.space_id === itemId);
            setUnassignedSpaces(prev => [...prev, { id: itemId, name: space?.name }]);
        } else {
            const menu = menus.find(m => m.menu_id === itemId);
            setUnassignedMenus(prev => [...prev, { id: itemId, name: menu?.name }]);
        }
    };

    const handleSave = () => {

        // TODO: Call backend when enabled
        toggle();
        navigate('/dashboard');
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered scrollable className="distribution-wizard-modal">
            <ModalHeader toggle={toggle} className="bg-light p-0 border-bottom">
                <div className="p-4 d-flex align-items-center gap-3">
                    <div className="p-2 bg-white rounded-3 border">
                        <ChefHat size={20} style={{ color: THEME_COLOR }} />
                    </div>
                    <div>
                        <h5 className="mb-0 fw-bold">Advanced Distribution</h5>
                        <p className="text-muted small mb-0">Configure station routing and menu display sequences.</p>
                    </div>
                </div>
                <Nav tabs className="px-4 border-0">
                    <NavItem>
                        <NavLink
                            className={`px-4 py-2 border-0 fw-bold ${activeTab === '1' ? 'active text-primary border-bottom border-primary' : 'text-muted'}`}
                            onClick={() => setActiveTab('1')}
                            style={{ cursor: 'pointer', borderBottom: activeTab === '1' ? `2px solid ${THEME_COLOR}` : 'none' }}
                        >
                            <Split size={16} className="me-2" /> Routing Matrix
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={`px-4 py-2 border-0 fw-bold ${activeTab === '2' ? 'active text-primary' : 'text-muted'}`}
                            onClick={() => setActiveTab('2')}
                            style={{ cursor: 'pointer', borderBottom: activeTab === '2' ? `2px solid ${THEME_COLOR}` : 'none' }}
                        >
                            <ListOrdered size={16} className="me-2" /> Menu Sequence
                        </NavLink>
                    </NavItem>
                </Nav>
            </ModalHeader>
            <ModalBody className="p-0 bg-light" style={{ minHeight: '600px' }}>
                {isLoadingData ? (
                    <div className="h-100 d-flex align-items-center justify-content-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                    </div>
                ) : (
                    <TabContent activeTab={activeTab} className="h-100">
                        <TabPane tabId="1" className="h-100">
                            <div className="d-flex h-100 overflow-hidden">
                                {/* Sidebar: Unassigned Assets */}
                                <div className="bg-white border-end p-3 d-flex flex-column gap-4" style={{ width: '300px' }}>
                                    {/* Unassigned Spaces */}
                                    <div className="flex-grow-1 overflow-hidden d-flex flex-column">
                                        <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                                            <label className="small text-uppercase fw-bold text-muted mb-0" style={{ fontSize: '10px' }}>Global Spaces ({unassignedSpaces.length})</label>
                                            <Badge color="soft-warning">FALLBACK</Badge>
                                        </div>
                                        <div className="overflow-auto pe-2 flex-grow-1">
                                            <AnimatePresence mode="popLayout">
                                                {unassignedSpaces.map(space => {
                                                    const isSelected = selectedItems.find(i => i.id === space.id && i.type === 'space');
                                                    return (
                                                        <motion.div
                                                            key={`unassigned-space-${space.id}`}
                                                            layout
                                                            draggable
                                                            onDragStart={(e) => {
                                                                const dragItems = isSelected ? selectedItems : [{ id: space.id, type: 'space' }];
                                                                if (!isSelected) toggleSelection(space.id, 'space');
                                                                handleDragStart(e, dragItems);
                                                            }}
                                                            onClick={() => toggleSelection(space.id, 'space')}
                                                            className={`p-2 rounded-3 mb-2 border d-flex align-items-center gap-2 cursor-grab pointer-event ${isSelected ? 'border-primary bg-soft-primary' : 'bg-white'}`}
                                                        >
                                                            <GripVertical size={14} className="text-muted" />
                                                            <span className="small fw-medium">{space.name}</span>
                                                        </motion.div>
                                                    );
                                                })}
                                            </AnimatePresence>
                                            {unassignedSpaces.length === 0 && <div className="text-center py-4 opacity-50 small border border-dashed rounded-3">All Assigned</div>}
                                        </div>
                                    </div>

                                    {/* Unassigned Menus */}
                                    <div className="flex-grow-1 overflow-hidden d-flex flex-column">
                                        <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                                            <label className="small text-uppercase fw-bold text-muted mb-0" style={{ fontSize: '10px' }}>Global Menus ({unassignedMenus.length})</label>
                                            <Badge color="soft-info">SMART</Badge>
                                        </div>
                                        <div className="overflow-auto pe-2 flex-grow-1">
                                            <AnimatePresence mode="popLayout">
                                                {unassignedMenus.map(menu => {
                                                    const isSelected = selectedItems.find(i => i.id === menu.id && i.type === 'menu');
                                                    return (
                                                        <motion.div
                                                            key={`unassigned-menu-${menu.id}`}
                                                            layout
                                                            draggable
                                                            onDragStart={(e) => {
                                                                const dragItems = isSelected ? selectedItems : [{ id: menu.id, type: 'menu' }];
                                                                if (!isSelected) toggleSelection(menu.id, 'menu');
                                                                handleDragStart(e, dragItems);
                                                            }}
                                                            onClick={() => toggleSelection(menu.id, 'menu')}
                                                            className={`p-2 rounded-3 mb-2 border d-flex align-items-center gap-2 cursor-grab ${isSelected ? 'border-primary bg-soft-primary' : 'bg-white'}`}
                                                        >
                                                            <GripVertical size={14} className="text-muted" />
                                                            <span className="small fw-medium text-truncate">{menu.name}</span>
                                                        </motion.div>
                                                    );
                                                })}
                                            </AnimatePresence>
                                            {unassignedMenus.length === 0 && <div className="text-center py-4 opacity-50 small border border-dashed rounded-3">All Assigned</div>}
                                        </div>
                                    </div>
                                </div>

                                {/* Main Canvas: Kitchen Stations */}
                                <div className="flex-grow-1 p-4 overflow-auto">
                                    <Row className="g-4">
                                        {stations.map((station, index) => (
                                            <Col key={station.id} md={6}>
                                                <div
                                                    onDragOver={(e) => { e.preventDefault(); setDragOverStationId(station.id); }}
                                                    onDragLeave={() => setDragOverStationId(null)}
                                                    onDrop={(e) => handleDrop(e, station.id)}
                                                    className={`bg-white rounded-4 shadow-sm border h-100 overflow-hidden d-flex flex-column transition-all ${dragOverStationId === station.id ? 'border-primary ring-2 ring-primary bg-soft-primary' : ''}`}
                                                    style={dragOverStationId === station.id ? { transform: 'scale(1.02)' } : {}}
                                                >
                                                    <div className="p-3 bg-light border-bottom d-flex align-items-center justify-content-between">
                                                        <h6 className="fw-bold mb-0">{station.name}</h6>
                                                        <Badge color="white" className="border text-muted">Station #{index + 1}</Badge>
                                                    </div>

                                                    <div className="row g-0 flex-grow-1" style={{ minHeight: '150px' }}>
                                                        <div className="col-6 border-end p-3">
                                                            <label className="small text-uppercase fw-bold text-muted mb-2 d-block" style={{ fontSize: '9px' }}>Linked Spaces</label>
                                                            <div className="d-flex flex-column gap-1">
                                                                {station.assignedSpaces.map(id => {
                                                                    const space = spaces.find(s => s.space_id === id);
                                                                    return (
                                                                        <div key={id} className="bg-light border rounded-2 p-1 px-2 d-flex justify-content-between align-items-center">
                                                                            <span style={{ fontSize: '11px' }} className="fw-medium text-truncate">{space?.name}</span>
                                                                            <button onClick={() => unassignItem(station.id, id, 'space')} className="btn btn-link p-0 text-danger border-0"><Minus size={12} /></button>
                                                                        </div>
                                                                    );
                                                                })}
                                                                {station.assignedSpaces.length === 0 && <div className="text-center py-4 border-2 border-dashed rounded-3 opacity-25 small">Drop here</div>}
                                                            </div>
                                                        </div>
                                                        <div className="col-6 p-3">
                                                            <label className="small text-uppercase fw-bold text-muted mb-2 d-block" style={{ fontSize: '9px' }}>Linked Menus</label>
                                                            <div className="d-flex flex-column gap-1">
                                                                {station.assignedMenus.map(id => {
                                                                    const menu = menus.find(m => m.menu_id === id);
                                                                    return (
                                                                        <div key={id} className="bg-light border rounded-2 p-1 px-2 d-flex justify-content-between align-items-center">
                                                                            <span style={{ fontSize: '11px' }} className="fw-medium text-truncate">{menu?.name}</span>
                                                                            <button onClick={() => unassignItem(station.id, id, 'menu')} className="btn btn-link p-0 text-danger border-0"><Minus size={12} /></button>
                                                                        </div>
                                                                    );
                                                                })}
                                                                {station.assignedMenus.length === 0 && <div className="text-center py-4 border-2 border-dashed rounded-3 opacity-25 small">Drop here</div>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </div>
                        </TabPane>

                        <TabPane tabId="2" className="h-100">
                            <div className="d-flex h-100 overflow-hidden">
                                {/* Sidebar: Space Selection */}
                                <div className="bg-white border-end p-3 overflow-auto" style={{ width: '300px' }}>
                                    <label className="small text-uppercase fw-bold text-muted mb-3 d-block" style={{ fontSize: '10px' }}>Available Spaces</label>
                                    <div className="d-flex flex-column gap-2">
                                        {spaces.map(space => (
                                            <button
                                                key={space.space_id}
                                                onClick={() => setActiveSpaceId(space.space_id)}
                                                className={`p-3 rounded-3 border text-start transition-all d-flex align-items-center justify-content-between ${activeSpaceId === space.space_id ? 'border-primary bg-soft-primary ring-1 ring-primary' : 'bg-white'}`}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <LayoutGrid size={16} className={activeSpaceId === space.space_id ? 'text-primary' : 'text-muted'} />
                                                    <span className={`small fw-bold ${activeSpaceId === space.space_id ? 'text-primary' : ''}`}>{space.name}</span>
                                                </div>
                                                <Badge color="light" className="text-muted border">ID: {space.space_id}</Badge>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Main Canvas: Menu Sorting */}
                                <div className="flex-grow-1 p-5 overflow-auto">
                                    <div className="mx-auto" style={{ maxWidth: '600px' }}>
                                        <div className="mb-4">
                                            <h6 className="fw-bold mb-1">Display Sequence</h6>
                                            <p className="text-muted small">Arrange the order in which menus appear for the selected space.</p>
                                        </div>

                                        {activeSpaceId ? (
                                            <DragDropContext onDragEnd={onMenuSortEnd}>
                                                <Droppable droppableId="menu-sequence">
                                                    {(provided) => (
                                                        <div {...provided.droppableProps} ref={provided.innerRef} className="d-flex flex-column gap-2">
                                                            {spaceMenuLinks[activeSpaceId]?.map((menuId, index) => {
                                                                const menu = menus.find(m => m.menu_id === menuId);
                                                                return (
                                                                    <Draggable key={menuId} draggableId={menuId.toString()} index={index}>
                                                                        {(provided, snapshot) => (
                                                                            <div
                                                                                ref={provided.innerRef}
                                                                                {...provided.draggableProps}
                                                                                {...provided.dragHandleProps}
                                                                                className={`p-3 bg-white border rounded-3 d-flex align-items-center gap-3 transition-shadow ${snapshot.isDragging ? 'shadow-lg border-primary' : 'shadow-sm'}`}
                                                                            >
                                                                                <div className="p-2 bg-light rounded-2 text-muted">
                                                                                    <GripVertical size={16} />
                                                                                </div>
                                                                                <Badge color="primary" className="rounded-pill p-1 px-2">{index + 1}</Badge>
                                                                                <span className="fw-medium text-dark">{menu?.name}</span>
                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                );
                                                            })}
                                                            {provided.placeholder}
                                                            {(!spaceMenuLinks[activeSpaceId] || spaceMenuLinks[activeSpaceId].length === 0) && (
                                                                <div className="text-center py-5 border-2 border-dashed rounded-4 bg-white opacity-50">
                                                                    <p className="mb-0">No menus linked to this space</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </DragDropContext>
                                        ) : (
                                            <div className="text-center py-5">
                                                <Info size={40} className="text-muted opacity-25 mb-3" />
                                                <p className="text-muted">Select a space from the sidebar to configure its menu order.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </TabPane>
                    </TabContent>
                )}
            </ModalBody>
            <ModalFooter className="p-3 bg-white border-top">
                <div className="container-fluid d-flex align-items-center justify-content-between p-0">
                    <div className="d-flex align-items-center gap-2">
                        <Info size={16} className="text-muted" />
                        <span className="small text-muted">Select multiple items to drag & drop. Stations can handle multiple spaces and menus.</span>
                    </div>
                    <div className="d-flex gap-2">
                        <Button color="light" onClick={toggle} className="rounded-pill px-4">Cancel</Button>
                        <Button color="success" onClick={handleSave} className="rounded-pill px-4">Save Changes</Button>
                    </div>
                </div>
            </ModalFooter>
        </Modal >
    );
};

export default DistributionWizardModal;
